import { formatDateTime } from '@pickleballinc/lib/format-dates';

import {
  GameStats,
  GameStatsDetail,
  GameWinningPointEntity,
  TeamOneMatchStatsOrTeamTwoMatchStats,
  TeamStats,
  TimeoutUsedEntity,
} from '@/modules/results/types';

interface TimeoutInfo {
  start: string;
  result?: string;
  TimeoutStarted?: string;
  TeamOneScore?: number;
  TeamTwoScore?: number;
  TimeoutCalledByUuid?: string;
  ServerWhenTimeoutCalledFullName?: string;
  NextRallyResult?: string;
}

interface PointStreak {
  streak: number | undefined;
  start: string;
  end: string;
  games: string[];
}

interface Serves {
  serves: number | undefined;
  servesPlayerOne: number | undefined;
  servesPlayerTwo: number | undefined;
}

interface TotalServerPointPlayerOne {
  firstServes: number;
  firstServesPlayerOne: number;
  firstServesPlayerTwo: number;
}

interface TotalSecondServerPointPlayerOne {
  secondServes: number;
  secondServesPlayerOne: number | undefined;
  secondServesPlayerTwo: number | undefined;
}

interface TotalWinRalliesWhenInServingPositionOne {
  winRalliesWhenInServingPosition: number;
  winRalliesWhenInServingPositionPlayerOne: number | undefined;
  winRalliesWhenInServingPositionPlayerTwo: number | undefined;
}

interface TotalLossRalliesWhenInServingPositionOne {
  lossRalliesWhenInServingPosition: number;
  lossRalliesWhenInServingPositionPlayerOne: number | undefined;
  lossRalliesWhenInServingPositionPlayerTwo: number | undefined;
}

interface TotalWinRalliesWhenReceivingServeOne {
  winReceivingServing: number | undefined;
  winReceivingServingPlayerOne: number | undefined;
  winReceivingServingPlayerTwo: number | undefined;
}

interface TotalLossRalliesWhenReceivingServeOne {
  lossReceivingServing: number | undefined;
  lossReceivingServingPlayerOne: number | undefined;
  lossReceivingServingPlayerTwo: number | undefined;
}

interface SideOuts {
  sideOuts: number | undefined;
}

interface GameStatistics {
  duration: {
    formattedDuration: string;
    formattedStartTime: string;
    formattedEndTime: string;
  };
  totalPointsWon: {
    teamOne: number;
    teamTwo: number;
  };
  timeoutsUsed: {
    teamOne: {
      number: number;
      timeouts: TimeoutInfo[];
    };
    teamTwo: {
      number: number;
      timeouts: TimeoutInfo[];
    };
  };
  gamePoints: {
    teamOne: GameWinningPointEntity[] | undefined;
    teamTwo: GameWinningPointEntity[] | undefined;
  };
  longestPointStreak: {
    teamOne: PointStreak;
    teamTwo: PointStreak;
  };
  noPointStreak: {
    teamOne: PointStreak;
    teamTwo: PointStreak;
  };
  sideOuts: {
    teamOne: SideOuts;
    teamTwo: SideOuts;
  };
  serves: {
    teamOne: Serves;
    teamTwo: Serves;
  };
  firstServes: {
    teamOne: TotalServerPointPlayerOne;
    teamTwo: TotalServerPointPlayerOne;
  };
  secondServes: {
    teamOne: TotalSecondServerPointPlayerOne;
    teamTwo: TotalSecondServerPointPlayerOne;
  };
  winRalliesServing: {
    teamOne: TotalWinRalliesWhenInServingPositionOne;
    teamTwo: TotalWinRalliesWhenInServingPositionOne;
  };
  lossRalliesServing: {
    teamOne: TotalLossRalliesWhenInServingPositionOne;
    teamTwo: TotalLossRalliesWhenInServingPositionOne;
  };
  winReceivingServing: {
    teamOne: TotalWinRalliesWhenReceivingServeOne;
    teamTwo: TotalWinRalliesWhenReceivingServeOne;
  };
  lossReceivingServing: {
    teamOne: TotalLossRalliesWhenReceivingServeOne;
    teamTwo: TotalLossRalliesWhenReceivingServeOne;
  };
}

export interface Statistics {
  [key: string]: GameStatistics;
}

export const calculateStatistics = (matchStatsData: GameStats) => {
  const utc = matchStatsData?.MatchInfo?.utc_offset;
  const abbreviation = matchStatsData?.MatchInfo?.timezone_abbreviation;

  const calculateGameStatistics = (matchStats: GameStats, gameNumber: number) => {
    const teamStatsGameNumber = matchStats.TeamOneStats[gameNumber] as GameStatsDetail;

    return {
      duration: calculateDuration(teamStatsGameNumber.GameStart, teamStatsGameNumber.GameEnd),
      totalPointsWon: calculateTotalPointsWonForGame(matchStats, gameNumber),
      timeoutsUsed: calculateTimeoutsUsedForGame(matchStats, gameNumber),
      gamePoints: calculateGamePointsForGame(matchStats, gameNumber),
      longestPointStreak: calculateLongestPointStreakForGame(matchStats, gameNumber),
      noPointStreak: calculateNoPointStreakForGame(matchStats, gameNumber),
      sideOuts: calculateTotalSideOutsForGame(matchStats, gameNumber),
      serves: calculateTotalServesForGame(matchStats, gameNumber),
      firstServes: calculateTotalFirstServerForGame(matchStats, gameNumber),
      secondServes: calculateTotalSecondServerForGame(matchStats, gameNumber),
      winRalliesServing: calculateTotalWinRalliesWhenInServingPositionForGame(
        matchStats,
        gameNumber
      ),
      lossRalliesServing: calculateTotalLossRalliesWhenInServingPositionForGame(
        matchStats,
        gameNumber
      ),
      winReceivingServing: calculateTotalWinRalliesWhenReceivingServeForGame(
        matchStats,
        gameNumber
      ),
      lossReceivingServing: calculateTotalLossRalliesWhenReceivingServeForGame(
        matchStats,
        gameNumber
      ),
    };
  };

  const calculateDuration = (start: string, end: string) => {
    const startTime = new Date(start).getTime();
    const endTime = new Date(end).getTime();
    let formattedDuration = '0';
    let formattedStartTime = '0';
    let formattedEndTime = '0';

    if (start && end) {
      const duration = (endTime - startTime) / 1000; // Duration in seconds
      const minutes = Math.floor(duration / 60);
      const seconds = Math.floor(duration % 60);
      formattedDuration = `${minutes} min ${seconds} sec`;
    }

    if (start) {
      formattedStartTime = formatDateTime(new Date(start).toISOString(), {
        utcOffset: utc,
        timezoneAbbreviation: abbreviation,
        type: 'time',
      });
    }
    if (end) {
      formattedEndTime = formatDateTime(new Date(end).toISOString(), {
        utcOffset: utc,
        timezoneAbbreviation: abbreviation,
        type: 'time',
      });
    }

    return {
      formattedDuration,
      formattedStartTime,
      formattedEndTime,
    };
  };

  const calculateTotalPointsWon = (matchStats: GameStats) => {
    let totalPointsTeamOne = 0;
    let totalPointsTeamTwo = 0;

    Object.keys(matchStats?.TeamOneStats || []).forEach((gameKey) => {
      const game = Number(gameKey);
      const team1StatsGameNumber = matchStats.TeamOneStats[game] as GameStatsDetail;
      const team2StatsGameNumber = matchStats.TeamTwoStats[game] as GameStatsDetail;

      totalPointsTeamOne += team1StatsGameNumber.TotalPoints;
      totalPointsTeamTwo += team2StatsGameNumber.TotalPoints;
    });

    return {
      teamOne: totalPointsTeamOne,
      teamTwo: totalPointsTeamTwo,
    };
  };

  const calculateTotalPointsWonForGame = (matchStats: GameStats, gameNumber: number) => {
    const team1StatsGameNumber = matchStats.TeamOneStats[gameNumber] as GameStatsDetail;
    const team2StatsGameNumber = matchStats.TeamTwoStats[gameNumber] as GameStatsDetail;
    return {
      teamOne: team1StatsGameNumber.TotalPoints,
      teamTwo: team2StatsGameNumber.TotalPoints,
    };
  };

  const calculateTimeoutsUsedForGame = (matchStats: GameStats, gameNumber: number) => {
    const calculateTimeoutInfo = (teamStats: TeamStats) => {
      const gameData = teamStats[gameNumber];
      if (!gameData || !gameData.TimeoutUsed || gameData.TimeoutUsed.length === 0) {
        return { number: 0, timeouts: [] };
      }

      const timeouts = gameData.TimeoutUsed.map((timeout: TimeoutUsedEntity) => ({
        start: formatDateTime(timeout.TimeoutStarted, {
          utcOffset: utc,
          timezoneAbbreviation: abbreviation,
          type: 'time',
          precision: 'sec',
        }),
        result: `${timeout.TeamOneScore}-${timeout.TeamTwoScore}`,
      }));

      return { number: timeouts.length, timeouts };
    };

    const timeoutsTeamOne = calculateTimeoutInfo(matchStats.TeamOneStats);
    const timeoutsTeamTwo = calculateTimeoutInfo(matchStats.TeamTwoStats);

    return {
      teamOne: timeoutsTeamOne,
      teamTwo: timeoutsTeamTwo,
    };
  };

  const calculateTimeoutsUsed = (matchStats: GameStats) => {
    const processTimeouts = (teamStats: TeamStats) => {
      let totalTimeouts = 0;
      const timeoutsInfo: TimeoutInfo[] = [];

      Object.keys(teamStats || []).forEach((gameNumber) => {
        const game = Number(gameNumber);
        const gameData = teamStats[game] as GameStatsDetail;
        if (!gameData || !gameData.TimeoutUsed || gameData.TimeoutUsed.length === 0) return;

        totalTimeouts += gameData.TimeoutUsed.length;

        gameData.TimeoutUsed.forEach((timeout: TimeoutUsedEntity) => {
          timeoutsInfo.push({
            start: formatDateTime(timeout.TimeoutStarted, {
              utcOffset: utc,
              timezoneAbbreviation: abbreviation,
              type: 'time',
              precision: 'sec',
            }),
            result: `Game ${gameNumber} | ${timeout.TeamOneScore}-${timeout.TeamTwoScore}`,
          });
        });
      });

      return { number: totalTimeouts, timeouts: timeoutsInfo };
    };

    const teamOneResult = processTimeouts(matchStats.TeamOneStats);
    const teamTwoResult = processTimeouts(matchStats.TeamTwoStats);

    return {
      teamOne: teamOneResult,
      teamTwo: teamTwoResult,
    };
  };

  const calculateGamePoints = (matchStats: GameStats) => {
    const gamePointsTeamOne = matchStats?.GameWinningPoint?.filter(
      (item: GameWinningPointEntity) => item.TeamOneScore > item.TeamTwoScore
    );
    const gamePointsTeamTwo = matchStats?.GameWinningPoint?.filter(
      (item: GameWinningPointEntity) => item.TeamOneScore < item.TeamTwoScore
    );
    return {
      teamOne: gamePointsTeamOne,
      teamTwo: gamePointsTeamTwo,
    };
  };

  const calculateGamePointsForGame = (matchStats: GameStats, gameNumber: number) => {
    const gameNo = Number(gameNumber);
    const gamePointsTeamOne = matchStats?.GameWinningPoint?.filter(
      (item: GameWinningPointEntity) =>
        item.TeamOneScore > item.TeamTwoScore && item.GameNumber === gameNo
    );
    const gamePointsTeamTwo = matchStats?.GameWinningPoint?.filter(
      (item: GameWinningPointEntity) =>
        item.TeamOneScore < item.TeamTwoScore && item.GameNumber === gameNo
    );
    return {
      teamOne: gamePointsTeamOne,
      teamTwo: gamePointsTeamTwo,
    };
  };

  const calculateLongestPointStreak = (matchStats: GameStats) => {
    const getLongestPointStreak = (
      teamStats: TeamStats,
      teamMatchStats: TeamOneMatchStatsOrTeamTwoMatchStats
    ): PointStreak => {
      const games: string[] = [];
      const longestPointStreak: PointStreak = {
        streak: teamMatchStats?.PointStreak,
        start: formatDateTime(teamMatchStats?.PointStreakStart, {
          utcOffset: utc,
          timezoneAbbreviation: abbreviation,
          type: 'time',
          precision: 'sec',
        }),
        end: formatDateTime(teamMatchStats?.PointStreakEnd, {
          utcOffset: utc,
          timezoneAbbreviation: abbreviation,
          type: 'time',
          precision: 'sec',
        }),
        games: [],
      };

      Object.keys(teamStats || {}).forEach((item) => {
        const gameNo = Number(item);

        const gameData = teamStats[gameNo];
        if (gameData) {
          const gameStart = formatDateTime(gameData.GameStart, {
            utcOffset: utc,
            timezoneAbbreviation: abbreviation,
            precision: 'sec',
          });
          const gameEnd = formatDateTime(gameData.GameEnd, {
            utcOffset: utc,
            timezoneAbbreviation: abbreviation,
            precision: 'sec',
          });

          if (gameStart <= longestPointStreak.end && gameEnd >= longestPointStreak.start) {
            games.push(`Game ${gameData.GameNumber}`);
          }
        }
      });

      longestPointStreak.games = games;
      return longestPointStreak;
    };

    const longestPointStreakOne = getLongestPointStreak(
      matchStats.TeamOneStats,
      matchStats.TeamOneMatchStats
    );
    const longestPointStreakTwo = getLongestPointStreak(
      matchStats.TeamTwoStats,
      matchStats.TeamTwoMatchStats
    );

    return {
      teamOne: longestPointStreakOne,
      teamTwo: longestPointStreakTwo,
    };
  };

  const calculateLongestPointStreakForGame = (matchStats: GameStats, gameNumber: number) => {
    const getPointStreakForGame = (teamStats: TeamStats) => {
      const teamStatsGameNumber = teamStats[gameNumber] as GameStatsDetail;
      return {
        streak: teamStats[gameNumber]?.PointStreak,
        start: formatDateTime(teamStatsGameNumber?.PointStreakStart, {
          utcOffset: utc,
          timezoneAbbreviation: abbreviation,
          type: 'time',
          precision: 'sec',
        }),
        end: formatDateTime(teamStatsGameNumber?.PointStreakStart, {
          utcOffset: utc,
          timezoneAbbreviation: abbreviation,
          type: 'time',
          precision: 'sec',
        }),
        games: [],
      };
    };

    const longestPointStreakOne = getPointStreakForGame(matchStats.TeamOneStats);
    const longestPointStreakTwo = getPointStreakForGame(matchStats.TeamTwoStats);

    return {
      teamOne: longestPointStreakOne,
      teamTwo: longestPointStreakTwo,
    };
  };

  const calculateNoPointStreak = (matchStats: GameStats) => {
    const getNoPointStreak = (
      teamStats: TeamStats,
      teamMatchStats: TeamOneMatchStatsOrTeamTwoMatchStats
    ) => {
      const games: string[] = [];
      const noPointStreak: PointStreak = {
        streak: teamMatchStats?.NoPointStreak,
        start: formatDateTime(teamMatchStats?.NoPointStreakStart, {
          utcOffset: utc,
          timezoneAbbreviation: abbreviation,
          type: 'time',
          precision: 'sec',
        }),
        end: formatDateTime(teamMatchStats?.NoPointStreakEnd, {
          utcOffset: utc,
          timezoneAbbreviation: abbreviation,
          type: 'time',
          precision: 'sec',
        }),
        games: [],
      };

      Object.keys(teamStats || {}).forEach((item) => {
        const gameNo = Number(item);

        const gameData = teamStats[gameNo];
        if (gameData) {
          const gameStart = formatDateTime(gameData.GameStart, {
            utcOffset: utc,
            timezoneAbbreviation: abbreviation,
            type: 'time',
            precision: 'sec',
          });
          const gameEnd = formatDateTime(gameData.GameEnd, {
            utcOffset: utc,
            timezoneAbbreviation: abbreviation,
            type: 'time',
            precision: 'sec',
          });

          if (gameStart <= noPointStreak.end && gameEnd >= noPointStreak.start) {
            games.push(`Game ${gameData.GameNumber}`);
          }
        }
      });

      noPointStreak.games = games;
      return noPointStreak;
    };

    const longestNoPointStreakOne = getNoPointStreak(
      matchStats.TeamOneStats,
      matchStats.TeamOneMatchStats
    );
    const longestNoPointStreakTwo = getNoPointStreak(
      matchStats.TeamTwoStats,
      matchStats.TeamTwoMatchStats
    );

    return {
      teamOne: longestNoPointStreakOne,
      teamTwo: longestNoPointStreakTwo,
    };
  };

  const calculateNoPointStreakForGame = (matchStats: GameStats, gameNumber: number) => {
    const getNoPointStreakForGame = (teamStats: TeamStats) => {
      const teamStatsGameNumber = teamStats[gameNumber] as GameStatsDetail;

      return {
        streak: teamStats[gameNumber]?.NoPointStreak,
        start: formatDateTime(teamStatsGameNumber?.NoPointStreakStart, {
          utcOffset: utc,
          timezoneAbbreviation: abbreviation,
          type: 'time',
          precision: 'sec',
        }),
        end: formatDateTime(teamStatsGameNumber?.NoPointStreakEnd, {
          utcOffset: utc,
          timezoneAbbreviation: abbreviation,
          type: 'time',
          precision: 'sec',
        }),
        games: [],
      };
    };

    const longestNoPointStreakOne = getNoPointStreakForGame(matchStats.TeamOneStats);
    const longestNoPointStreakTwo = getNoPointStreakForGame(matchStats.TeamTwoStats);

    return {
      teamOne: longestNoPointStreakOne,
      teamTwo: longestNoPointStreakTwo,
    };
  };

  const calculateTotalSideOuts = (matchStats: GameStats) => {
    const getTotalSideOuts = (teamMatchStats: TeamOneMatchStatsOrTeamTwoMatchStats) => {
      const totalSideOuts = {
        sideOuts: teamMatchStats?.TotalSideouts,
      };
      return totalSideOuts;
    };
    const totalSideOutsOne = getTotalSideOuts(matchStats.TeamOneMatchStats);
    const totalSideOutsTwo = getTotalSideOuts(matchStats.TeamTwoMatchStats);
    return {
      teamOne: totalSideOutsOne,
      teamTwo: totalSideOutsTwo,
    };
  };

  const calculateTotalSideOutsForGame = (matchStats: GameStats, gameNumber: number) => {
    const getTotalSideOutsForGame = (teamStats: TeamStats) => {
      return {
        sideOuts: teamStats[gameNumber]?.TotalSideouts,
      };
    };

    const totalSideOutsOne = getTotalSideOutsForGame(matchStats.TeamOneStats);
    const totalSideOutsTwo = getTotalSideOutsForGame(matchStats.TeamTwoStats);

    return {
      teamOne: totalSideOutsOne,
      teamTwo: totalSideOutsTwo,
    };
  };

  const calculateTotalServes = (matchStats: GameStats) => {
    const getTotalServes = (teamMatchStats: TeamOneMatchStatsOrTeamTwoMatchStats) => {
      const totalServes = {
        serves: teamMatchStats?.TotalReceiveServe,
        servesPlayerOne: teamMatchStats?.TotalServesPlayerOne,
        servesPlayerTwo: teamMatchStats?.TotalServesPlayerTwo,
      };
      return totalServes;
    };
    const totalServesOne = getTotalServes(matchStats.TeamOneMatchStats);
    const totalServesTwo = getTotalServes(matchStats.TeamTwoMatchStats);
    return {
      teamOne: totalServesOne,
      teamTwo: totalServesTwo,
    };
  };

  const calculateTotalServesForGame = (matchStats: GameStats, gameNumber: number) => {
    const getTotalServesForGame = (teamStats: TeamStats) => {
      return {
        serves: teamStats[gameNumber]?.TotalReceiveServe,
        servesPlayerOne: teamStats[gameNumber]?.TotalServesPlayerOne,
        servesPlayerTwo: teamStats[gameNumber]?.TotalServesPlayerTwo,
      };
    };

    const totalServesOne = getTotalServesForGame(matchStats.TeamOneStats);
    const totalServesTwo = getTotalServesForGame(matchStats.TeamTwoStats);

    return {
      teamOne: totalServesOne,
      teamTwo: totalServesTwo,
    };
  };

  const calculateTotalFirstServer = (matchStats: GameStats) => {
    const getTotalFirstServerPointPlayer = (
      teamMatchStats: TeamOneMatchStatsOrTeamTwoMatchStats
    ) => {
      const totalFirstServer = {
        firstServes:
          teamMatchStats?.TotalFirstServerPointPlayerOne +
          teamMatchStats?.TotalFirstServerPointPlayerTwo,
        firstServesPlayerOne: teamMatchStats?.TotalFirstServerPointPlayerOne,
        firstServesPlayerTwo: teamMatchStats?.TotalFirstServerPointPlayerTwo,
      };
      return totalFirstServer;
    };
    const totalFirstServerPointPlayerOne = getTotalFirstServerPointPlayer(
      matchStats.TeamOneMatchStats
    );
    const totalFirstServerPointPlayerTwo = getTotalFirstServerPointPlayer(
      matchStats.TeamTwoMatchStats
    );
    return {
      teamOne: totalFirstServerPointPlayerOne,
      teamTwo: totalFirstServerPointPlayerTwo,
    };
  };

  const calculateTotalFirstServerForGame = (matchStats: GameStats, gameNumber: number) => {
    const getTotalFirstServerForGame = (teamStats: TeamStats) => {
      const teamStatsGameNumber = teamStats[gameNumber] as GameStatsDetail;
      return {
        firstServes:
          teamStatsGameNumber?.TotalFirstServerPointPlayerOne +
          teamStatsGameNumber?.TotalFirstServerPointPlayerTwo,
        firstServesPlayerOne: teamStatsGameNumber?.TotalFirstServerPointPlayerOne,
        firstServesPlayerTwo: teamStatsGameNumber?.TotalFirstServerPointPlayerTwo,
      };
    };

    const totalFirstServerPointPlayerOne = getTotalFirstServerForGame(matchStats.TeamOneStats);
    const totalFirstServerPointPlayerTwo = getTotalFirstServerForGame(matchStats.TeamTwoStats);

    return {
      teamOne: totalFirstServerPointPlayerOne,
      teamTwo: totalFirstServerPointPlayerTwo,
    };
  };

  const calculateTotalSecondServer = (matchStats: GameStats) => {
    const getTotalSecondServerPointPlayer = (
      teamMatchStats: TeamOneMatchStatsOrTeamTwoMatchStats
    ) => {
      const totalFirstServer = {
        secondServes:
          teamMatchStats?.TotalSecondServerPointPlayerOne +
          teamMatchStats?.TotalSecondServerPointPlayerTwo,
        secondServesPlayerOne: teamMatchStats?.TotalSecondServerPointPlayerOne,
        secondServesPlayerTwo: teamMatchStats?.TotalSecondServerPointPlayerTwo,
      };
      return totalFirstServer;
    };
    const totalSecondServerPointPlayerOne = getTotalSecondServerPointPlayer(
      matchStats.TeamOneMatchStats
    );
    const totalSecondServerPointPlayerTwo = getTotalSecondServerPointPlayer(
      matchStats.TeamTwoMatchStats
    );
    return {
      teamOne: totalSecondServerPointPlayerOne,
      teamTwo: totalSecondServerPointPlayerTwo,
    };
  };

  const calculateTotalSecondServerForGame = (matchStats: GameStats, gameNumber: number) => {
    const getTotalSecondServerForGame = (teamStats: TeamStats) => {
      const teamStatsGameNumber = teamStats[gameNumber] as GameStatsDetail;

      return {
        secondServes:
          teamStatsGameNumber?.TotalSecondServerPointPlayerOne +
          teamStatsGameNumber?.TotalSecondServerPointPlayerTwo,
        secondServesPlayerOne: teamStats[gameNumber]?.TotalSecondServerPointPlayerOne,
        secondServesPlayerTwo: teamStats[gameNumber]?.TotalSecondServerPointPlayerTwo,
      };
    };

    const totalSecondServerPointPlayerOne = getTotalSecondServerForGame(matchStats.TeamOneStats);
    const totalSecondServerPointPlayerTwo = getTotalSecondServerForGame(matchStats.TeamTwoStats);

    return {
      teamOne: totalSecondServerPointPlayerOne,
      teamTwo: totalSecondServerPointPlayerTwo,
    };
  };

  const calculateTotalWinRalliesWhenInServingPosition = (matchStats: GameStats) => {
    const getTotalWinRalliesWhenInServingPosition = (
      teamMatchStats: TeamOneMatchStatsOrTeamTwoMatchStats
    ) => {
      const totalWinRallies = {
        winRalliesWhenInServingPosition:
          teamMatchStats?.TotalWinRalliesWhenInServingPositionPlayerOne +
          teamMatchStats?.TotalWinRalliesWhenInServingPositionPlayerTwo,
        winRalliesWhenInServingPositionPlayerOne:
          teamMatchStats?.TotalWinRalliesWhenInServingPositionPlayerOne,
        winRalliesWhenInServingPositionPlayerTwo:
          teamMatchStats?.TotalWinRalliesWhenInServingPositionPlayerTwo,
      };
      return totalWinRallies;
    };
    const totalWinRalliesWhenInServingPositionOne = getTotalWinRalliesWhenInServingPosition(
      matchStats.TeamOneMatchStats
    );
    const totalWinRalliesWhenInServingPositionTwo = getTotalWinRalliesWhenInServingPosition(
      matchStats.TeamTwoMatchStats
    );
    return {
      teamOne: totalWinRalliesWhenInServingPositionOne,
      teamTwo: totalWinRalliesWhenInServingPositionTwo,
    };
  };

  const calculateTotalWinRalliesWhenInServingPositionForGame = (
    matchStats: GameStats,
    gameNumber: number
  ) => {
    const getTotalWinRalliesWhenInServingPosition = (teamStats: TeamStats) => {
      const teamStatsGameNumber = teamStats[gameNumber] as GameStatsDetail;

      return {
        winRalliesWhenInServingPosition:
          teamStatsGameNumber?.TotalWinRalliesWhenInServingPositionPlayerOne +
          teamStatsGameNumber?.TotalWinRalliesWhenInServingPositionPlayerTwo,
        winRalliesWhenInServingPositionPlayerOne:
          teamStats[gameNumber]?.TotalWinRalliesWhenInServingPositionPlayerOne,
        winRalliesWhenInServingPositionPlayerTwo:
          teamStats[gameNumber]?.TotalWinRalliesWhenInServingPositionPlayerTwo,
      };
    };

    const totalWinRalliesWhenInServingPositionOne = getTotalWinRalliesWhenInServingPosition(
      matchStats.TeamOneStats
    );
    const totalWinRalliesWhenInServingPositionTwo = getTotalWinRalliesWhenInServingPosition(
      matchStats.TeamTwoStats
    );

    return {
      teamOne: totalWinRalliesWhenInServingPositionOne,
      teamTwo: totalWinRalliesWhenInServingPositionTwo,
    };
  };

  const calculateTotalLossRalliesWhenInServingPosition = (matchStats: GameStats) => {
    const getTotalLossRalliesWhenInServingPosition = (
      teamMatchStats: TeamOneMatchStatsOrTeamTwoMatchStats
    ) => {
      const totalLossRallies = {
        lossRalliesWhenInServingPosition:
          teamMatchStats?.TotalLossRalliesWhenInServingPositionPlayerOne +
          teamMatchStats?.TotalLossRalliesWhenInServingPositionPlayerTwo,
        lossRalliesWhenInServingPositionPlayerOne:
          teamMatchStats?.TotalLossRalliesWhenInServingPositionPlayerOne,
        lossRalliesWhenInServingPositionPlayerTwo:
          teamMatchStats?.TotalLossRalliesWhenInServingPositionPlayerTwo,
      };
      return totalLossRallies;
    };
    const totalLossRalliesWhenInServingPositionOne = getTotalLossRalliesWhenInServingPosition(
      matchStats.TeamOneMatchStats
    );
    const totalLossRalliesWhenInServingPositionTwo = getTotalLossRalliesWhenInServingPosition(
      matchStats.TeamTwoMatchStats
    );
    return {
      teamOne: totalLossRalliesWhenInServingPositionOne,
      teamTwo: totalLossRalliesWhenInServingPositionTwo,
    };
  };

  const calculateTotalLossRalliesWhenInServingPositionForGame = (
    matchStats: GameStats,
    gameNumber: number
  ) => {
    const getTotalLossRalliesWhenInServingPosition = (teamStats: TeamStats) => {
      const teamStatsGameNumber = teamStats[gameNumber] as GameStatsDetail;

      return {
        lossRalliesWhenInServingPosition:
          teamStatsGameNumber?.TotalLossRalliesWhenInServingPositionPlayerOne +
          teamStatsGameNumber?.TotalLossRalliesWhenInServingPositionPlayerTwo,
        lossRalliesWhenInServingPositionPlayerOne:
          teamStats[gameNumber]?.TotalLossRalliesWhenInServingPositionPlayerOne,
        lossRalliesWhenInServingPositionPlayerTwo:
          teamStats[gameNumber]?.TotalLossRalliesWhenInServingPositionPlayerTwo,
      };
    };

    const totalLossRalliesWhenInServingPositionOne = getTotalLossRalliesWhenInServingPosition(
      matchStats.TeamOneStats
    );
    const totalLossRalliesWhenInServingPositionTwo = getTotalLossRalliesWhenInServingPosition(
      matchStats.TeamTwoStats
    );

    return {
      teamOne: totalLossRalliesWhenInServingPositionOne,
      teamTwo: totalLossRalliesWhenInServingPositionTwo,
    };
  };

  const calculateTotalWinRalliesWhenReceivingServe = (matchStats: GameStats) => {
    const getTotalWinRalliesWhenReceivingServe = (
      teamMatchStats: TeamOneMatchStatsOrTeamTwoMatchStats
    ) => {
      const WinRalliesWhenReceivingServe = {
        winReceivingServing: teamMatchStats?.TotalWinRalliesWhenReceivingServe,
        winReceivingServingPlayerOne: teamMatchStats?.TotalWinRalliesWhenReceivingServePlayerOne,
        winReceivingServingPlayerTwo: teamMatchStats?.TotalWinRalliesWhenReceivingServePlayerTwo,
      };
      return WinRalliesWhenReceivingServe;
    };
    const totalWinRalliesWhenReceivingServeOne = getTotalWinRalliesWhenReceivingServe(
      matchStats.TeamOneMatchStats
    );
    const totalWinRalliesWhenReceivingServeTwo = getTotalWinRalliesWhenReceivingServe(
      matchStats.TeamTwoMatchStats
    );
    return {
      teamOne: totalWinRalliesWhenReceivingServeOne,
      teamTwo: totalWinRalliesWhenReceivingServeTwo,
    };
  };

  const calculateTotalWinRalliesWhenReceivingServeForGame = (
    matchStats: GameStats,
    gameNumber: number
  ) => {
    const getTotalWinRalliesWhenReceivingServe = (teamStats: TeamStats) => {
      return {
        winReceivingServing: teamStats[gameNumber]?.TotalWinRalliesWhenReceivingServe,
        winReceivingServingPlayerOne:
          teamStats[gameNumber]?.TotalWinRalliesWhenReceivingServePlayerOne,
        winReceivingServingPlayerTwo:
          teamStats[gameNumber]?.TotalWinRalliesWhenReceivingServePlayerTwo,
      };
    };

    const totalWinRalliesWhenReceivingServeOne = getTotalWinRalliesWhenReceivingServe(
      matchStats.TeamOneStats
    );
    const totalWinRalliesWhenReceivingServeTwo = getTotalWinRalliesWhenReceivingServe(
      matchStats.TeamTwoStats
    );

    return {
      teamOne: totalWinRalliesWhenReceivingServeOne,
      teamTwo: totalWinRalliesWhenReceivingServeTwo,
    };
  };

  const calculateTotalLossRalliesWhenReceivingServe = (matchStats: GameStats) => {
    const getTotalLossRalliesWhenReceivingServe = (
      teamMatchStats: TeamOneMatchStatsOrTeamTwoMatchStats
    ) => {
      const LossRalliesWhenReceivingServe = {
        lossReceivingServing: teamMatchStats?.TotalLossRalliesWhenReceivingServe,
        lossReceivingServingPlayerOne: teamMatchStats?.TotalLossRalliesWhenReceivingServePlayerOne,
        lossReceivingServingPlayerTwo: teamMatchStats?.TotalLossRalliesWhenReceivingServePlayerTwo,
      };
      return LossRalliesWhenReceivingServe;
    };
    const totalLossRalliesWhenReceivingServeOne = getTotalLossRalliesWhenReceivingServe(
      matchStats.TeamOneMatchStats
    );
    const totalLossRalliesWhenReceivingServeTwo = getTotalLossRalliesWhenReceivingServe(
      matchStats.TeamTwoMatchStats
    );
    return {
      teamOne: totalLossRalliesWhenReceivingServeOne,
      teamTwo: totalLossRalliesWhenReceivingServeTwo,
    };
  };

  const calculateTotalLossRalliesWhenReceivingServeForGame = (
    matchStats: GameStats,
    gameNumber: number
  ) => {
    const getTotalLossRalliesWhenReceivingServe = (teamStats: TeamStats) => {
      return {
        lossReceivingServing: teamStats[gameNumber]?.TotalLossRalliesWhenReceivingServe,
        lossReceivingServingPlayerOne:
          teamStats[gameNumber]?.TotalLossRalliesWhenReceivingServePlayerOne,
        lossReceivingServingPlayerTwo:
          teamStats[gameNumber]?.TotalLossRalliesWhenReceivingServePlayerTwo,
      };
    };

    const totalLossRalliesWhenReceivingServeOne = getTotalLossRalliesWhenReceivingServe(
      matchStats.TeamOneStats
    );
    const totalLossRalliesWhenReceivingServeTwo = getTotalLossRalliesWhenReceivingServe(
      matchStats.TeamTwoStats
    );

    return {
      teamOne: totalLossRalliesWhenReceivingServeOne,
      teamTwo: totalLossRalliesWhenReceivingServeTwo,
    };
  };

  const statistics: Statistics = {
    Match: {
      duration: calculateDuration(
        matchStatsData.MatchInfo.match_start,
        matchStatsData.MatchInfo.match_completed
      ),
      totalPointsWon: calculateTotalPointsWon(matchStatsData),
      timeoutsUsed: calculateTimeoutsUsed(matchStatsData),
      gamePoints: calculateGamePoints(matchStatsData),
      longestPointStreak: calculateLongestPointStreak(matchStatsData),
      noPointStreak: calculateNoPointStreak(matchStatsData),
      sideOuts: calculateTotalSideOuts(matchStatsData),
      serves: calculateTotalServes(matchStatsData),
      firstServes: calculateTotalFirstServer(matchStatsData),
      secondServes: calculateTotalSecondServer(matchStatsData),
      winRalliesServing: calculateTotalWinRalliesWhenInServingPosition(matchStatsData),
      lossRalliesServing: calculateTotalLossRalliesWhenInServingPosition(matchStatsData),
      winReceivingServing: calculateTotalWinRalliesWhenReceivingServe(matchStatsData),
      lossReceivingServing: calculateTotalLossRalliesWhenReceivingServe(matchStatsData),
    },
  };

  Object.keys(matchStatsData?.TeamOneStats || []).forEach((gameNumber) => {
    const gameNo = Number(gameNumber);
    const gameNoKey = `Game${gameNo}` as keyof Statistics;

    statistics[gameNoKey] = calculateGameStatistics(matchStatsData, gameNo);
  });

  return statistics;
};
